import type { RiskClass, RunStatus, StepCaptions, StreamEvent, TrustLabel } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `trace` — the operator's instrument, in words.
 *
 * Moved here from `components/trace/TracePanel.tsx`, which never had a catalogue of its own
 * (PRD-27 F-14). The panel is this product's signature surface: most assistants hide their
 * machinery behind a "details" disclosure, and Ralph Chat is deployed inside a company's
 * network specifically because the organization has to be able to trust what it says, so the
 * machinery is co-equal with the answer.
 *
 * ── This is the register that IS allowed to say how the machine works ──
 *
 * The conversation column says what an answer is made of, in a reader's words; this says
 * what the machine did, in the machine's (AGM-022). `lexical`, `semantic`, `retrieval`,
 * `model` and the trust-boundary taxonomy belong here and nowhere else — and the same
 * distance is kept in Portuguese. An administrator is entitled to the technical name of the
 * thing they are running (D-15), which is also why `tok`, `ttft` and `cache` stay English
 * inside Portuguese sentences.
 *
 * ── Enums are words, never identifiers ──
 *
 * Every label map below is `satisfies Record<TheEnum, string>`, which is the entire
 * mechanism: a new member of a contract enum becomes a compile error in this file rather
 * than a raw `remote_agent_content` on the surface whose job is to be read carefully
 * (D-9, LOC-018). The English is exactly what the underscore-stripping these replaced
 * produced, so nothing on screen moved when they landed.
 *
 * Each fallback word is copy too: `run-stream-reducer.ts` widens `risk`, `label` and
 * `dimension` to `string` on the way in, so an unrecognised value says it is unrecognised
 * rather than printing its token — the trust boundary is what this panel exists to show, and
 * a segment that looked unattributed would be worse than one labelled "unlabelled".
 *
 * English is the source of truth AND the type (LOC-011). Anything that interpolates is a
 * FUNCTION returning a whole sentence (LOC-013).
 */

/** See the note in `chat.ts`: structural, so this file does not close a circle on `I18n`. */
type Counting = { locale: Locale; formatCount: (value: number) => string }

const plural = (
  fmt: Counting,
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }
): string => forms[pluralCategory(fmt.locale, count)] ?? forms.other

/**
 * The budget dimension, derived from the stream contract instead of retyped, so a sixth
 * dimension on the wire is a compile error here — the `?? dimension` fallback this replaced
 * leaked `wall_clock_ms` at a reader the moment one landed.
 *
 * Exported because `components/trace/trace-labels.ts` needs it for the runtime guard that
 * reads this map. The type belongs with the map it guards, and `i18n/` is the lower layer:
 * components depend on the catalogue, never the other way round.
 */
export type BudgetDimension = Extract<StreamEvent, { type: 'budget.warning' }>['dimension']

export const trace = {
  // --- the panel itself ----------------------------------------------------

  /** The landmark's accessible name. */
  panelLabel: 'Run trace',
  title: 'Trace',
  /** The pulsing dot is decoration; the state is announced as text a screen reader reaches. */
  inProgress: 'Run in progress',
  /** Two readouts in the header. `tok` is the term of art, abbreviated (D-15). */
  totalTokens: 'tok',
  totalCost: 'cost',
  close: 'Close the run trace',
  empty:
    'Every model call, search and tool use appears here as it happens — with the model that ran, what it retrieved, and what it cost.',

  // --- one node on the rail ------------------------------------------------

  /**
   * A step's caption, from the code the server named rather than from the prose it wrote
   * (PRD-27 D-8, LOC-042). The English here is word for word what the orchestrator generates,
   * so nothing on this panel moved when the codes landed.
   *
   * This is the OPERATOR's wording. The same six steps are worded again, differently, in
   * `chat.stepActivity` for the conversation column, which may not say how the machine is
   * configured (AGM-022) — which is why `model_call` names the model profile here and names
   * nothing there.
   *
   * `satisfies StepCaptions` is the whole mechanism: a seventh code on the wire is a compile
   * error in this file rather than a blank caption on the panel the product is named for.
   *
   * The forms are mixed — one gerund, four imperatives — because that is what the
   * orchestrator already wrote and this panel must not move. Portuguese mirrors each entry's
   * form rather than imposing one on all six. None of them agrees with a noun, which is what
   * lets one caption serve a step that is running and the same step read back months later.
   */
  stepLabel: {
    /** The alias is a model profile: data, shown verbatim, never translated (D-17). */
    model_call: ({ alias }) => `Reasoning with ${alias}`,
    knowledge_search: () => 'Search knowledge base',
    memory_write: () => 'Remember a preference',
    announcement: () => 'Send announcement',
    delegation: () => 'Delegate to a remote agent',
    /**
     * A tool this platform does not ship — an MCP capability — named by the server that
     * provides it. That name is data and is rendered verbatim in both languages (D-17); the
     * entry exists so the lookup is total, not so there is something to translate.
     */
    tool_call: ({ tool }) => tool
  } satisfies StepCaptions,

  /**
   * A risk class as a word. The same six values are worded again in `approvals.risk`, on
   * purpose (D-9 as amended): the consent card and the trace read them in different frames
   * of mind, and a shared map would be a file every namespace writer has to edit.
   *
   * In Portuguese all six agree with a feminine noun — `ação`, `classe de risco` — so they
   * are a single-gender set and need no per-entity variant.
   */
  risk: {
    read: 'read',
    reversible_write: 'reversible write',
    external_communication: 'external communication',
    privileged: 'privileged',
    financial: 'financial',
    destructive: 'destructive'
  } satisfies Record<RiskClass, string>,
  riskUnknown: 'unclassified',

  /** How many passages a retrieval kept. It read "1 passages" for every single-passage step. */
  passages: (fmt: Counting, kept: number): string =>
    plural(fmt, kept, { one: `${fmt.formatCount(kept)} passage`, other: `${fmt.formatCount(kept)} passages` }),

  nodeFailed: 'failed',
  /** A model node shows WHICH tool it reached for; the arrow is the whole sentence. */
  toolReached: (toolName: string): string => `→ ${toolName}`,
  /**
   * The failure on a node. `code` is a machine identifier and never translates (D-17); the
   * sentence beside it arrives already rendered — from the failure's `messageKey` where there
   * is one, from the server's English where there is not (PRD-27 D-4, LOC-033).
   */
  nodeError: (code: string, message: string): string => `${code}: ${message}`,

  // --- a model call --------------------------------------------------------

  modelIn: 'in',
  modelOut: 'out',
  /** `cache` is a term of art and stays English inside a Portuguese sentence (D-15). */
  modelCached: 'cached',
  /** Time to first token. An acronym an operator reads in English in every tool they use. */
  modelTtft: 'ttft',
  modelStop: 'stop',

  contextTitle: 'Context assembled',
  /** The tilde is the hedge: a segment's token count is an estimate and says so. */
  contextTokens: (tokens: string): string => `~${tokens}`,

  /**
   * What the model was allowed to treat as instruction rather than as data. The boundary is
   * the point of the panel, so an unrecognised label says so rather than rendering nothing.
   */
  trust: {
    platform_instruction: 'platform instruction',
    skill_instruction: 'skill instruction',
    user_input: 'user input',
    user_memory: 'user memory',
    retrieved_document: 'retrieved document',
    tool_result: 'tool result',
    remote_agent_content: 'remote agent content'
  } satisfies Record<TrustLabel, string>,
  trustUnknown: 'unlabelled',

  /** Tool names are identifiers and never translate; only the list joining is localized. */
  toolsOffered: (tools: string): string => `tools offered: ${tools}`,

  // --- a retrieval ---------------------------------------------------------

  retrievalLexical: 'lexical',
  retrievalSemantic: 'semantic',
  retrievalFused: 'fused',
  retrievalKept: 'kept',

  resultMarker: (position: number): string => `[${position}]`,
  /**
   * Which arm found a passage, and where it ranked there. This is what turns a result list
   * into a diagnosis: a passage found only semantically ranks differently than one both arms
   * agreed on.
   */
  resultLexicalRank: (rank: number): string => `lex #${rank}`,
  resultSemanticRank: (rank: number): string => `sem #${rank}`,

  // --- a tool call ---------------------------------------------------------

  toolResult: (summary: string): string => `→ ${summary}`,

  // --- budgets -------------------------------------------------------------

  /**
   * Shown while the run is still going, not only in the receipt. A run that stops short
   * because it reached a limit looks exactly like one that decided it was finished; saying
   * which limit, and how close it got, is the difference between "the assistant gave up" and
   * "the assistant was cut off at 8 of 8 tool calls".
   */
  budgetTitle: 'Approaching its budget',
  dimension: {
    steps: 'steps',
    model_calls: 'model calls',
    tool_calls: 'tool calls',
    cost_usd: 'cost',
    wall_clock_ms: 'time'
  } satisfies Record<BudgetDimension, string>,
  dimensionUnknown: 'budget',
  /** "8 of 8". Both sides are already formatted; only the word between them is copy. */
  budgetAmount: (used: string, limit: string): string => `${used} of ${limit}`,

  // --- the close-out -------------------------------------------------------

  outcomeTitle: 'Outcome',
  /**
   * The same eleven values as `chat.runStatus`, worded for the operator rather than the
   * reader (D-9 as amended). In Portuguese every one of them describes an `execução`, which
   * is FEMININE — "concluída", never "concluído".
   */
  runStatus: {
    queued: 'queued',
    running: 'running',
    waiting_for_approval: 'waiting for approval',
    waiting_for_input: 'waiting for input',
    waiting_for_external: 'waiting for external',
    suspended: 'suspended',
    completed: 'completed',
    partially_completed: 'partially completed',
    failed: 'failed',
    cancelled: 'cancelled',
    expired: 'expired'
  } satisfies Record<RunStatus, string>,

  outcomeSteps: 'steps',
  outcomeModel: 'model',
  outcomeTools: 'tools',
  outcomeTime: 'time',
  outcomeCost: 'cost',

  /**
   * Completed and failed effects are listed separately and always — a run that did half its
   * work must not be able to present itself as a success.
   */
  outcomeCompleted: 'Completed',
  outcomeIncomplete: 'Did not complete',
  outcomeCaveats: 'Caveats'
}
